import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useSession } from '@/entities/session';
import { ResendButton } from '@/features/profile';
import { resendVerification } from '@/shared/api/auth';
import type { User } from '@/shared/api/types';

import styles from './Header.module.css';
import { UserMenu } from './UserMenu';

/** Reminds a user with an unconfirmed address, with a way to get a new link. */
function VerifyBanner({ user }: { user: User }): React.JSX.Element {
  const { t } = useTranslation();
  const mutation = useMutation({ mutationFn: resendVerification });

  return (
    <div className={styles.banner} role="status">
      <p>{t('auth.verifyBanner', { email: user.email })}</p>
      <ResendButton
        pending={mutation.isPending}
        sent={mutation.isSuccess}
        onClick={() => {
          mutation.mutate();
        }}
      />
    </div>
  );
}

/**
 * Brand on the left, the account menu on the right, and the section
 * navigation between them for a signed-in user. The navigation comes in as a
 * slot because it is a widget of its own.
 */
export function Header({ nav }: { nav?: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();

  return (
    <>
      <header className={styles.header}>
        <Link className={styles.brand} to="/">
          {t('brand')}
        </Link>
        {session.data && nav}
        {session.data && <UserMenu user={session.data} />}
      </header>
      {session.data && !session.data.email_verified && <VerifyBanner user={session.data} />}
    </>
  );
}
